import React, { useEffect, useMemo, useState } from 'react';
import { Button, Col, Modal, Row, Skeleton, Tag, Typography } from 'antd';
import {
  BarChartOutlined,
  CheckCircleOutlined,
  LockOutlined,
  LoginOutlined,
  PayCircleOutlined,
  ReadOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { SUBMIT_PAYMENT_ENABLED } from '../utils/site';
import { usePageSeo } from '../utils/usePageSeo';
import { useLanguage } from '../store/LanguageContext';

const { Title, Paragraph, Text } = Typography;

interface InsightArticle {
  title: string;
  summary: string;
  category: string;
  date: string;
  readTime: string;
  free: boolean;
  url?: string;
}

/** 离线兜底；优先读 /content/insights.json（日更脚本产出） */
const fallbackArticles: InsightArticle[] = [
  {
    title: '中小企业选择AI写作工具的五项评估指标',
    summary: '从内容质量、团队协作、数据留存、价格结构和售后支持五个维度，帮助企业快速筛选适合内部使用的AI写作服务。',
    category: '工具选型',
    date: '2026-05-06',
    readTime: '6分钟',
    free: true,
  },
  {
    title: 'AI图像生成平台在电商素材制作中的落地流程',
    summary: '围绕商品图、活动海报、详情页配图三个场景，拆解从提示词规范到人工复核的实际操作步骤。',
    category: '业务实践',
    date: '2026-05-03',
    readTime: '8分钟',
    free: true,
  },
  {
    title: '客服场景接入智能问答系统前需要准备哪些知识库',
    summary: '整理常见问答、产品规则、订单说明、售后口径等资料清单，并说明上线前的测试和迭代方法。',
    category: '知识库',
    date: '2026-04-29',
    readTime: '7分钟',
    free: true,
  },
  {
    title: '2026年AI办公订阅预算测算表与采购建议',
    summary: '结合常见团队规模和使用频率，测算不同工具组合的月度成本，并给出预算审批时可复用的说明结构。',
    category: '付费报告',
    date: '2026-04-25',
    readTime: '12分钟',
    free: false,
  },
  {
    title: '企业内部AI工具合规评估清单',
    summary: '覆盖账号权限、数据输入边界、结果复核、供应商资质、合同条款等关键检查项，适合上线前逐项确认。',
    category: '付费清单',
    date: '2026-04-20',
    readTime: '10分钟',
    free: false,
  },
  {
    title: '研发团队引入代码助手后的效率复盘方法',
    summary: '提供可量化的复盘指标，包括需求交付周期、缺陷修复时间、评审返工率和知识沉淀效果。',
    category: '付费分析',
    date: '2026-04-16',
    readTime: '11分钟',
    free: false,
  },
];

const InsightsPage: React.FC = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  usePageSeo({
    title: language === 'zh-CN' ? '行业洞察与选型入口' : 'Industry insights & buying notes',
    description: language === 'zh-CN'
      ? '免费选型短文与 AI 工具观察。按写作、出图、视频、编程等任务阅读，并标注国内可用性。'
      : 'Free buying notes on AI tools by task — coding, images, video, and mainland China access.',
    path: '/insights',
  });
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [activeArticle, setActiveArticle] = useState<InsightArticle | null>(null);
  const [articles, setArticles] = useState<InsightArticle[]>([]);
  const [insightsLoading, setInsightsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/content/insights.json?t=${Date.now()}`, { cache: 'no-store' });
        if (!res.ok) throw new Error('insights fetch failed');
        const data = await res.json();
        const list = Array.isArray(data?.articles) ? data.articles : [];
        if (!cancelled) {
          setArticles(
            list.length > 0
              ? list.map((a: InsightArticle & { url?: string }) => ({
                  title: a.title,
                  summary: a.summary,
                  category: a.category || '行业洞察',
                  date: a.date,
                  readTime: a.readTime || '5分钟',
                  free: a.free !== false,
                  url: a.url,
                }))
              : fallbackArticles,
          );
        }
      } catch {
        if (!cancelled) setArticles(fallbackArticles);
      } finally {
        if (!cancelled) setInsightsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const featured = useMemo(() => articles.filter((item) => item.free !== false).slice(0, 6), [articles]);
  const paid = useMemo(() => articles.filter((item) => item.free === false), [articles]);
  const zh = language === 'zh-CN';

  const openPayment = (article: InsightArticle) => {
    setActiveArticle(article);
    setPaymentOpen(true);
  };

  return (
    <div className="insights-page">
      <section className="insights-hero">
        <div className="insights-hero-inner">
          <div>
            <Tag color="blue" className="insights-eyebrow">{zh ? '选型入口' : 'Buying notes'}</Tag>
            <Title level={1}>{zh ? '先按任务看短文' : 'Start with a short buying note'}</Title>
            <Paragraph>
              {zh
                ? '这里是精选入口：点卡片进资讯正文。完整列表在「选型短文」。'
                : 'A curated gateway into buying notes. Full list lives under Buying notes.'}
            </Paragraph>
            <Button type="primary" onClick={() => navigate('/news')} style={{ marginTop: 8 }}>
              {zh ? '打开全部选型短文' : 'Open all buying notes'}
            </Button>
          </div>
          <div className="insights-stat-panel" aria-label="资料库概览">
            <div>
              <strong>{featured.length}{zh ? '篇' : ''}</strong>
              <span>{zh ? '入口精选' : 'Featured'}</span>
            </div>
            {SUBMIT_PAYMENT_ENABLED ? (
              <>
                <div>
                  <strong>{paid.length}{zh ? '篇' : ''}</strong>
                  <span>{zh ? '付费资料' : 'Paid'}</span>
                </div>
                <div>
                  <strong>{zh ? '每篇9.9元' : '$1.4'}</strong>
                  <span>{zh ? '浏览信息收费' : 'Paywalled'}</span>
                </div>
              </>
            ) : (
              <div>
                <strong>{zh ? '暂缓' : 'Paused'}</strong>
                <span>{zh ? '付费阅读' : 'Paid reading'}</span>
              </div>
            )}
          </div>
        </div>
      </section>

      <main className="insights-container">
        <section className="insights-section">
          <div className="insights-section-head">
            <div>
              <Title level={2}>{zh ? '精选选型短文' : 'Featured buying notes'}</Title>
              <Paragraph>{zh ? '点「读全文」打开资讯正文；要看更多请进选型短文列表。' : 'Open the article, or browse the full list.'}</Paragraph>
            </div>
            <ReadOutlined />
          </div>

          <Row gutter={[18, 18]}>
            {insightsLoading && featured.length === 0
              ? [0, 1, 2].map((i) => (
                  <Col xs={24} md={8} key={`sk-${i}`}>
                    <article className="insight-card">
                      <Skeleton active paragraph={{ rows: 3 }} />
                    </article>
                  </Col>
                ))
              : featured.map((article) => (
              <Col xs={24} md={8} key={article.title}>
                <article className="insight-card">
                  <div className="insight-card-meta">
                    <Tag color="green">免费</Tag>
                    <span>{article.date}</span>
                  </div>
                  <Title level={4}>{article.title}</Title>
                  <Paragraph>{article.summary}</Paragraph>
                  <div className="insight-card-footer">
                    <Text type="secondary">{article.category} · {article.readTime}</Text>
                    <Button
                      type="link"
                      onClick={() => {
                        if (article.url && /\/news\//.test(article.url)) {
                          const path = article.url.replace(/^https?:\/\/[^/]+/, '');
                          navigate(path);
                          return;
                        }
                        Modal.info({
                          title: article.title,
                          content: (
                            <div>
                              <Paragraph>{article.summary}</Paragraph>
                              <Paragraph>
                                完整选型短文在「资讯」栏目。也可打开站内资讯列表继续阅读。
                              </Paragraph>
                              <Button type="link" onClick={() => navigate('/news')}>
                                去资讯列表
                              </Button>
                            </div>
                          ),
                          okText: '知道了',
                        });
                      }}
                    >
                      {article.url && /\/news\//.test(article.url) ? '读全文' : '查看'}
                    </Button>
                  </div>
                </article>
              </Col>
            ))}
          </Row>
        </section>

        {SUBMIT_PAYMENT_ENABLED ? (
        <>
        <section className="insights-section">
          <div className="insights-section-head">
            <div>
              <Title level={2}>付费浏览资料</Title>
              <Paragraph>可在列表中看到资料摘要，扫码付费后进入登录或注册页面继续阅读。</Paragraph>
            </div>
            <PayCircleOutlined />
          </div>

          <Row gutter={[18, 18]}>
            {paid.map((article) => (
              <Col xs={24} md={8} key={article.title}>
                <article className="insight-card paid">
                  <div className="insight-card-meta">
                    <Tag color="gold">浏览信息收费</Tag>
                    <span>{article.date}</span>
                  </div>
                  <Title level={4}>{article.title}</Title>
                  <Paragraph>{article.summary}</Paragraph>
                  <div className="insight-lock-line">
                    <LockOutlined />
                    <span>完整内容需付费后查看</span>
                  </div>
                  <div className="payment-preview">
                    <img src="/payment-qr/wechat_qr.svg" alt="微信支付二维码" />
                    <div>
                      <Text strong>扫码支付 9.9 元</Text>
                      <Text type="secondary">二维码下方收费提示：浏览信息收费，支付后请登录或注册。</Text>
                    </div>
                  </div>
                  <Button type="primary" icon={<PayCircleOutlined />} block onClick={() => openPayment(article)}>
                    扫码付费阅读
                  </Button>
                </article>
              </Col>
            ))}
          </Row>
        </section>

        <section className="insights-notes">
          <div>
            <CheckCircleOutlined />
            <span>本资料库不强制登录即可访问首页和免费内容。</span>
          </div>
          <div>
            <BarChartOutlined />
            <span>付费内容展示摘要，付款后再进入账号登录或注册流程。</span>
          </div>
        </section>
        </>
        ) : (
        <section className="insights-notes">
          <div>
            <CheckCircleOutlined />
            <span>{zh ? '免费短文无需登录即可阅读。' : 'Free notes need no login.'}</span>
          </div>
          <div>
            <ReadOutlined />
            <span>
              {zh ? '完整日更列表在' : 'Full daily list:'}{' '}
              <Button type="link" style={{ padding: 0, height: 'auto' }} onClick={() => navigate('/news')}>
                {zh ? '选型短文' : 'Buying notes'}
              </Button>
            </span>
          </div>
        </section>
        )}
      </main>

      {SUBMIT_PAYMENT_ENABLED && (
      <Modal
        open={paymentOpen}
        title="浏览信息收费"
        okText="去登录或注册"
        cancelText="稍后再说"
        onOk={() => navigate('/login')}
        onCancel={() => setPaymentOpen(false)}
      >
        <div className="insights-pay-modal">
          <img src="/payment-qr/wechat_qr.svg" alt="微信支付二维码" />
          <Paragraph>
            正在购买：<Text strong>{activeArticle?.title}</Text>
          </Paragraph>
          <Paragraph>
            请使用微信扫码支付 9.9 元。支付完成后点击“去登录或注册”，即可进入账号页面继续查看付费资料。
          </Paragraph>
          <Button icon={<LoginOutlined />} onClick={() => navigate('/login')}>
            已支付，前往登录/注册
          </Button>
        </div>
      </Modal>
      )}
    </div>
  );
};

export default InsightsPage;
